import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, TrendingDown, HelpCircle, X, Check } from 'lucide-react';
export function Problem() {
  const problems = [{
    icon: <AlertTriangle className="w-8 h-8 text-accent" data-id="element-104" />,
    title: 'Cansado del boca a boca',
    desc: 'Dependés de que un cliente te recomiende a otro. No tenés un sistema predecible de adquisición de nuevos clientes.'
  }, {
    icon: <TrendingDown className="w-8 h-8 text-accent" data-id="element-105" />,
    title: 'Ventas inestables',
    desc: 'Un mes bien, dos mal. No podés planificar ni invertir con confianza porque el flujo de clientes es impredecible.'
  }, {
    icon: <HelpCircle className="w-8 h-8 text-accent" data-id="element-106" />,
    title: 'Sin estrategia clara',
    desc: 'No sabés qué estrategias aplicar, cuáles funcionan para el rubro gastronómico ni cómo medir resultados reales.'
  }];
  const currentSituation = ['No sabés qué estrategias aplicar', 'Dependés del boca en boca', 'No tenés un sistema claro', 'Flujo de clientes irregular', 'Ventas impredecibles'];
  const resultSituation = ['Sistemas de ventas funcionando', 'Mayor ticket promedio de venta', 'Crecimiento predecible y sostenido', 'Flujo constante de comensales', 'Facturación escalable x10'];
  return <section id="el-problema" className="py-24 bg-gradient-1 relative" data-id="element-107">
      <div className="absolute inset-0 bg-grid-pattern opacity-10 pointer-events-none" data-id="element-108" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-109">
        <div className="text-center mb-16" data-id="element-110">
          <motion.span initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-accent text-sm font-bold tracking-widest uppercase mb-4 block" data-id="element-111">
            EL PROBLEMA
          </motion.span>
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-4xl md:text-5xl font-heading font-bold text-white" data-id="element-112">
            ¿Te identificás con esto?
          </motion.h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-24" data-id="element-113">
          {problems.map((prob, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: idx * 0.1
        }} className="bg-[rgba(255,50,50,0.05)] border border-[rgba(255,50,50,0.2)] rounded-xl p-8 hover:border-accent/40 hover:shadow-[0_0_20px_rgba(255,107,0,0.1)] transition-all duration-300 group" data-id="element-114">
              <div className="mb-6 bg-black/30 w-16 h-16 rounded-lg flex items-center justify-center border border-white/5 group-hover:border-accent/30 transition-colors" data-id="element-115">
                {prob.icon}
              </div>
              <h3 className="text-xl font-bold text-white mb-4" data-id="element-116">
                {prob.title}
              </h3>
              <p className="text-text-secondary leading-relaxed" data-id="element-117">{prob.desc}</p>
            </motion.div>)}
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="max-w-5xl mx-auto" data-id="element-118">
          <div className="text-center mb-10" data-id="element-119">
            <h3 className="text-3xl font-heading font-bold text-white mb-2" data-id="element-120">
              Dónde estás vs. Dónde podés estar
            </h3>
            <p className="text-text-secondary text-lg" data-id="element-121">
              La transformación que generamos
            </p>
          </div>

          <div className="flex flex-col lg:flex-row items-stretch gap-6 lg:gap-0 bg-[#111] rounded-2xl overflow-hidden border border-white/10" data-id="element-122">
            {/* Left Column */}
            <div className="flex-1 p-8 lg:p-12 bg-red-950/20 relative" data-id="element-123">
              <div className="absolute top-0 left-0 w-full h-1 bg-red-500/50" data-id="element-124" />
              <h4 className="text-red-400 font-bold text-sm tracking-wider mb-8" data-id="element-125">
                SITUACIÓN ACTUAL
              </h4>
              <ul className="space-y-6" data-id="element-126">
                {currentSituation.map((item, idx) => <li key={idx} className="flex items-start gap-4" data-id="element-127">
                    <div className="mt-1 bg-red-500/20 p-1 rounded" data-id="element-128">
                      <X className="w-4 h-4 text-red-500" data-id="element-129" />
                    </div>
                    <span className="text-gray-300" data-id="element-130">{item}</span>
                  </li>)}
              </ul>
            </div>

            {/* Center Arrow */}
            <div className="hidden lg:flex items-center justify-center -mx-6 z-10" data-id="element-131">
              <div className="bg-accent w-12 h-12 rounded-full flex items-center justify-center shadow-[0_0_20px_rgba(255,107,0,0.5)]" data-id="element-132">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-133">
                  <path d="M5 12h14M12 5l7 7-7 7" data-id="element-134" />
                </svg>
              </div>
            </div>

            {/* Right Column */}
            <div className="flex-1 p-8 lg:p-12 bg-green-950/20 relative" data-id="element-135">
              <div className="absolute top-0 left-0 w-full h-1 bg-accent" data-id="element-136" />
              <h4 className="text-accent font-bold text-sm tracking-wider mb-8" data-id="element-137">
                RESULTADO
              </h4>
              <ul className="space-y-6" data-id="element-138">
                {resultSituation.map((item, idx) => <li key={idx} className="flex items-start gap-4" data-id="element-139">
                    <div className="mt-1 bg-accent/20 p-1 rounded" data-id="element-140">
                      <Check className="w-4 h-4 text-accent" data-id="element-141" />
                    </div>
                    <span className="text-white font-medium" data-id="element-142">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}